import {
  groupKitControllerCreateKit,
  groupKitDocumentControllerCreateDocument,
  groupKitTemplateLinkControllerLink,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { Template } from "@/types/types";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const useCreateProjectWithDocumentAction = (
  groupUuid: string | null,
) => {
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  const create = async (template?: Template) => {
    if (!groupUuid) {
      throw new Error("Group UUID is required");
    }

    setIsCreating(true);

    try {
      // 1. Create project kit
      const kit = await groupKitControllerCreateKit({
        path: {
          groupUuid,
        },
        body: {
          name: "Untitled Workbook",
          type: "project",
        },
      }).then((res) => res.data);

      if (!kit) {
        throw new Error("Failed to create workbook");
      }

      // 2. Link template if provided
      if (template) {
        await groupKitTemplateLinkControllerLink({
          path: {
            groupUuid,
            kitUuid: kit.uuid,
            templateUuid: template.uuid,
          },
        });
      }

      // 3. Create blank document
      const document = await groupKitDocumentControllerCreateDocument({
        path: {
          kitUuid: kit.uuid,
          groupUuid: groupUuid,
        },
        body: {
          name: template?.name ?? "Untitled Document",
          templateUuid: template ? template.uuid : undefined,
        },
      }).then((res) => res.data);

      if (!document) {
        throw new Error("Failed to create document");
      }

      // 4. Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid),
      });

      return {
        kit: kit,
        document: document,
      };
    } catch (error) {
      toast.error("An error occurred while creating blank document.");
      throw error;
    }
  };

  return {
    create,
    isCreating,
  };
};
